import { useMemo, useState } from "react";
import type { PerWordResult, RawHit, VerseMeta } from "../core/types";
import { wordColor } from "../core/palette";
import type { SelectedHit } from "./HeatMap";

export type Hit = RawHit;

export interface ResultsListProps {
  results: PerWordResult[];
  verses: VerseMeta[];
  selected: SelectedHit | null;
  onSelect: (sel: SelectedHit) => void;
}

function findVerse(verses: VerseMeta[], compactStart: number): VerseMeta | null {
  if (verses.length === 0) return null;
  let lo = 0;
  let hi = verses.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >>> 1;
    if (verses[mid]!.compactStart <= compactStart) lo = mid;
    else hi = mid - 1;
  }
  return verses[lo] ?? null;
}

interface WordGroupProps {
  word: PerWordResult;
  wordIndex: number;
  verses: VerseMeta[];
  selected: SelectedHit | null;
  onSelect: (sel: SelectedHit) => void;
  defaultExpanded: boolean;
}

const MAX_HITS_PER_GROUP_RENDER = 200;

function WordGroup(props: WordGroupProps): JSX.Element {
  const { word, wordIndex, verses, selected, onSelect } = props;
  const [expanded, setExpanded] = useState(props.defaultExpanded);

  const sortedIndices = useMemo(() => {
    const idx = word.hits.map((_, i) => i);
    idx.sort((a, b) => Math.abs(word.hits[a]!.d) - Math.abs(word.hits[b]!.d));
    return idx;
  }, [word.hits]);

  const color = wordColor(wordIndex);

  return (
    <div className="result-group">
      <button
        className="result-group-header"
        onClick={() => setExpanded((e) => !e)}
      >
        <span className="result-swatch" style={{ background: color }} />
        <span className="result-group-word" dir="rtl" lang="he">
          {word.canonical || "(empty)"}
        </span>
        <span className="result-group-count">
          {word.hits.length}
          {word.truncated ? "+" : ""} hit{word.hits.length === 1 ? "" : "s"}
        </span>
        <span className="result-group-toggle">{expanded ? "▾" : "▸"}</span>
      </button>
      {expanded && (
        <div className="result-group-rows">
          {sortedIndices.length === 0 ? (
            <div className="results-empty">No hits.</div>
          ) : (
            sortedIndices.slice(0, MAX_HITS_PER_GROUP_RENDER).map((hitIndex) => {
              const h = word.hits[hitIndex]!;
              const verse = findVerse(verses, h.p);
              const isSelected =
                selected !== null &&
                selected.wordIndex === wordIndex &&
                selected.hitIndex === hitIndex;
              return (
                <button
                  key={hitIndex}
                  className={`result-row${isSelected ? " selected" : ""}`}
                  onClick={() => onSelect({ wordIndex, hitIndex })}
                >
                  <span
                    className="result-swatch small"
                    style={{ background: color }}
                  />
                  <span className="result-d">d = {h.d}</span>
                  <span className="result-citation">
                    {verse
                      ? `${verse.book} ${verse.chapter}:${verse.verse}`
                      : `pos ${h.p}`}
                  </span>
                </button>
              );
            })
          )}
          {sortedIndices.length > MAX_HITS_PER_GROUP_RENDER && (
            <div className="results-truncated">
              + {sortedIndices.length - MAX_HITS_PER_GROUP_RENDER} more (use the
              heatmap to inspect)
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function ResultsList(props: ResultsListProps): JSX.Element {
  const { results, verses, selected, onSelect } = props;
  return (
    <div className="results-list">
      <div className="results-header">
        <span className="results-title">Results</span>
      </div>
      <div className="results-scroll">
        {results.length === 0 ? (
          <div className="results-empty">No words yet.</div>
        ) : (
          results.map((word, wi) => (
            <WordGroup
              key={`${word.canonical}-${wi}`}
              word={word}
              wordIndex={wi}
              verses={verses}
              selected={selected}
              onSelect={onSelect}
              defaultExpanded={wi < 3}
            />
          ))
        )}
      </div>
    </div>
  );
}
